import type { ToolKey } from '@/types/radar'
import { detailRegistry } from '@/components/tools/result/detailRegistry'

/**
 * Dashboard tool-detail dispatcher (Phase 3 — final form, Step 15).
 *
 * Every dashboard `ToolKey` is registered in `detailRegistry`: the `TOOLS` list
 * and the registry share the same 13 keys, and `DetailsTab` only ever expands a
 * `ToolKey`, so a registered renderer always handles the tool. The fallback
 * below is therefore unreachable in practice — it stays only because
 * `detailRegistry` is a `Partial<Record<ToolKey, …>>`, which types the lookup
 * as possibly-`undefined`. It is a minimal safe net, NOT a renderer: the old
 * rich generic panel (header / AI summary / score breakdown / recommendations,
 * plus the local `MiniStat`) was removed once all tools migrated to the
 * registry (Steps 9–14). Do not reintroduce it.
 */
export function ToolDetailPanel({
  toolKey,
  data,
  toolIcon: Icon,
  toolColor,
  toolName,
}: {
  toolKey: ToolKey
  data: Record<string, unknown>
  toolIcon: React.ComponentType<{ size?: number; style?: React.CSSProperties }>
  toolColor: string
  toolName: string
}) {
  const Registered = detailRegistry[toolKey]
  if (Registered) {
    return (
      <Registered
        toolKey={toolKey}
        data={data}
        toolIcon={Icon}
        toolColor={toolColor}
        toolName={toolName}
      />
    )
  }

  // Unreachable safety net (see header) — keeps the scroll anchor, not the old
  // rich renderer.
  return (
    <div
      id='tool-detail-panel'
      className='rounded-xl border border-border bg-card p-5 text-sm text-muted-foreground'
    >
      No detailed view is available for {toolName}.
    </div>
  )
}
